/**
 * Ambience beds: the continuous sound of a place, one per `AmbienceProfile`.
 *
 * A bed is a handful of filtered noise sources and drones plus, for most
 * levels, the fluorescent hum. What keeps them from reading as loops is that
 * every layer is modulated by very slow LFOs at rates that never line up, and
 * that the incidental detail — drips, distant metal — is scheduled at random
 * intervals around the listener rather than baked into the bed.
 *
 * These are deliberately quiet. The player should be able to walk for ten
 * minutes hearing almost nothing and still feel like the building is running.
 */

import type { AmbienceProfile, Vec2 } from '../core/types';
import type { AudioGraph, NoiseKind } from './graph';
import { Hum } from './hum';
import type { Sfx, SpotKind } from './sfx';

export interface Bed {
  /** Layer gain. The ambience cross-fader owns this. */
  readonly out: GainNode;
  /** How much of this bed goes to the reverb. */
  readonly send: number;
  update(dt: number, unease: number): void;
  dispose(): void;
}

class BedBase implements Bed {
  readonly out: GainNode;
  send = 0.24;

  protected hum: Hum | null = null;
  protected readonly started: AudioScheduledSourceNode[] = [];
  /** Reused for spot positions so scheduling allocates nothing. */
  protected readonly spotAt: Vec2 = { x: 0, z: 0 };

  constructor(
    protected readonly g: AudioGraph,
    protected readonly sfx: Sfx,
  ) {
    this.out = g.ctx.createGain();
    this.out.gain.value = 0;
  }

  update(dt: number, unease: number): void {
    if (this.hum) {
      this.hum.setUnease(unease);
      this.hum.update(dt);
    }
  }

  dispose(): void {
    const t = this.g.now;
    for (const s of this.started) {
      try {
        s.stop(t);
      } catch {
        /* already stopped */
      }
    }
    this.started.length = 0;
    this.hum?.dispose();
    this.hum = null;
    this.out.disconnect();
  }

  /** A looping noise layer through one filter. Returns its gain for modulation. */
  protected noise(kind: NoiseKind, type: BiquadFilterType, freq: number, q: number, level: number): GainNode {
    const ctx = this.g.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.g.buffer(kind);
    src.loop = true;
    // Detuning the playback rate stops two layers reading the same buffer from
    // phasing against each other.
    src.playbackRate.value = this.g.rng.range(0.92, 1.08);
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = level;
    src.connect(f);
    f.connect(gain);
    gain.connect(this.out);
    src.start(this.g.now, this.g.rng.range(0, 2));
    this.started.push(src);
    return gain;
  }

  /** A held oscillator through a low-pass. Returns its gain for modulation. */
  protected drone(freq: number, type: OscillatorType, level: number, lpf: number, q = 1): GainNode {
    const ctx = this.g.ctx;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    osc.detune.value = this.g.rng.range(-9, 9);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = lpf;
    f.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = level;
    osc.connect(f);
    f.connect(gain);
    gain.connect(this.out);
    osc.start(this.g.now);
    this.started.push(osc);
    return gain;
  }

  protected lfo(rate: number, depth: number, target: AudioParam): void {
    const ctx = this.g.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = rate;
    const amp = ctx.createGain();
    amp.gain.value = depth;
    osc.connect(amp);
    amp.connect(target);
    osc.start(this.g.now);
    this.started.push(osc);
  }

  protected addHum(level: number, variant: number, flicker: number): void {
    this.hum = new Hum(this.g, this.out, variant, flicker);
    this.hum.setLevel(level);
  }

  /** Drop a one-shot somewhere around the listener. */
  protected fireSpot(kind: SpotKind, minDist: number, maxDist: number, gain = 1): void {
    const r = this.g.rng;
    const a = r.range(0, Math.PI * 2);
    const d = r.range(minDist, maxDist);
    this.spotAt.x = this.g.listener.x + Math.cos(a) * d;
    this.spotAt.z = this.g.listener.z + Math.sin(a) * d;
    this.sfx.spot(kind, this.spotAt, gain);
  }
}

/** Level 0. Carpet, low ceiling, and that hum. */
class LobbyBed extends BedBase {
  constructor(g: AudioGraph, sfx: Sfx) {
    super(g, sfx);
    this.send = 0.2;
    this.addHum(1, 0, 1);
    this.noise('brown', 'lowpass', 220, 0.5, 0.03);
    const air = this.noise('pink', 'bandpass', 700, 0.6, 0.012);
    this.lfo(0.037, 0.005, air.gain);
  }
}

/** Level 1. Concrete spans, plant running somewhere below, metal settling. */
class WarehouseBed extends BedBase {
  private metalIn: number;
  private creakIn: number;

  constructor(g: AudioGraph, sfx: Sfx) {
    super(g, sfx);
    this.send = 0.3;
    this.addHum(0.45, 3, 0.6);

    const a = this.drone(41, 'sawtooth', 0.03, 130, 1.4);
    const b = this.drone(61.5, 'sawtooth', 0.02, 120, 1.2);
    this.lfo(0.019, 0.008, a.gain);
    this.lfo(0.027, 0.006, b.gain);

    this.noise('brown', 'lowpass', 180, 0.6, 0.05);
    const rush = this.noise('pink', 'bandpass', 400, 0.4, 0.018);
    this.lfo(0.061, 0.009, rush.gain);

    this.metalIn = g.rng.range(14, 40);
    this.creakIn = g.rng.range(20, 55);
  }

  override update(dt: number, unease: number): void {
    super.update(dt, unease);
    this.metalIn -= dt;
    if (this.metalIn <= 0) {
      this.metalIn = this.g.rng.range(22, 65);
      this.fireSpot('metal', 14, 34, this.g.rng.range(0.4, 0.9));
    }
    this.creakIn -= dt;
    if (this.creakIn <= 0) {
      this.creakIn = this.g.rng.range(26, 78);
      this.fireSpot('creak', 8, 26, this.g.rng.range(0.5, 1));
    }
  }
}

/** Level 2. Tight, wet, and always dripping somewhere just out of reach. */
class PipesBed extends BedBase {
  private dripIn: number;
  private hissIn: number;

  constructor(g: AudioGraph, sfx: Sfx) {
    super(g, sfx);
    this.send = 0.35;
    this.addHum(0.2, 7, 1.6);

    // Boxy and close: the walls are an arm's length away in every direction.
    this.noise('pink', 'bandpass', 240, 1.6, 0.05);
    this.noise('brown', 'lowpass', 90, 0.7, 0.035);
    const hiss = this.noise('white', 'highpass', 2600, 0.7, 0.018);
    this.lfo(0.059, 0.01, hiss.gain);

    this.dripIn = g.rng.range(1, 4);
    this.hissIn = g.rng.range(10, 30);
  }

  override update(dt: number, unease: number): void {
    super.update(dt, unease);
    this.dripIn -= dt;
    if (this.dripIn <= 0) {
      this.dripIn = this.g.rng.range(1.6, 6.5);
      this.fireSpot('drip', 1.5, 9, this.g.rng.range(0.5, 1));
    }
    this.hissIn -= dt;
    if (this.hissIn <= 0) {
      this.hissIn = this.g.rng.range(14, 44);
      this.fireSpot('hiss', 3, 14, this.g.rng.range(0.4, 0.8));
    }
  }
}

/** Level 37. Warm, bright, wide, and almost pleasant, which is its own problem. */
class PoolroomsBed extends BedBase {
  private plinkIn: number;
  private dripIn: number;

  constructor(g: AudioGraph, sfx: Sfx) {
    super(g, sfx);
    this.send = 0.5;

    // Two bands swelling out of phase read as water moving against tile.
    const low = this.noise('white', 'bandpass', 420, 1.1, 0.022);
    const high = this.noise('white', 'bandpass', 900, 1.6, 0.014);
    this.lfo(0.11, 0.018, low.gain);
    this.lfo(0.077, 0.012, high.gain);

    this.noise('pink', 'highpass', 900, 0.6, 0.009);

    // A chord so quiet it reads as the room rather than as music.
    for (const f of [659.3, 988, 1318.5]) {
      const g2 = this.drone(f, 'sine', 0.0035, 4000);
      this.lfo(0.023 + f * 1e-5, 0.0025, g2.gain);
    }

    this.plinkIn = g.rng.range(4, 14);
    this.dripIn = g.rng.range(8, 22);
  }

  override update(dt: number, unease: number): void {
    super.update(dt, unease);
    this.plinkIn -= dt;
    if (this.plinkIn <= 0) {
      this.plinkIn = this.g.rng.range(5, 19);
      this.fireSpot('plink', 2, 13, this.g.rng.range(0.4, 0.9));
    }
    this.dripIn -= dt;
    if (this.dripIn <= 0) {
      this.dripIn = this.g.rng.range(9, 26);
      this.fireSpot('drip', 3, 16, this.g.rng.range(0.4, 0.8));
    }
  }
}

/** Title and menus. The building, heard from somewhere outside it. */
class MenuBed extends BedBase {
  constructor(g: AudioGraph, sfx: Sfx) {
    super(g, sfx);
    this.send = 0.28;
    this.addHum(0.25, 5, 0.4);
    const a = this.drone(55, 'sine', 0.022, 900);
    const b = this.drone(82.5, 'sine', 0.014, 900);
    this.lfo(0.017, 0.008, a.gain);
    this.lfo(0.029, 0.006, b.gain);
    this.noise('pink', 'lowpass', 900, 0.5, 0.01);
  }
}

/** Not digital silence: a dead room still has a floor. */
class SilenceBed extends BedBase {
  constructor(g: AudioGraph, sfx: Sfx) {
    super(g, sfx);
    this.send = 0.12;
    this.noise('pink', 'lowpass', 400, 0.5, 0.004);
  }
}

export function makeBed(profile: AmbienceProfile, g: AudioGraph, sfx: Sfx): Bed {
  switch (profile) {
    case 'warehouse':
      return new WarehouseBed(g, sfx);
    case 'pipes':
      return new PipesBed(g, sfx);
    case 'poolrooms':
      return new PoolroomsBed(g, sfx);
    case 'menu':
      return new MenuBed(g, sfx);
    case 'silence':
      return new SilenceBed(g, sfx);
    case 'lobby':
    default:
      return new LobbyBed(g, sfx);
  }
}
